import { useEffect, useState } from "react";
import { Volume2, VolumeX, Play, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Slider } from "./ui/slider";
import { getVoiceCfg, setVoiceCfg, speakTest, voiceSupported, getPrintComandas, setPrintComandas } from "../lib/voice";

export function VoiceSettings({ variant = "sidebar" }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(getVoiceCfg());
  const [printOn, setPrintOn] = useState(getPrintComandas());

  useEffect(() => { if (open) { setCfg(getVoiceCfg()); setPrintOn(getPrintComandas()); } }, [open]);

  const save = (next) => { setCfg(next); setVoiceCfg(next); };

  const isHeader = variant === "header";
  const trigger = isHeader ? (
    <button data-testid="voice-settings-open" onClick={() => setOpen(true)} className="p-2 text-[#E85D04]" title="Alertas de voz">
      {cfg.enabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
    </button>
  ) : (
    <button data-testid="voice-settings-open" onClick={() => setOpen(true)}
      className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-white/80 hover:bg-white/10 transition-colors">
      {cfg.enabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
      <span className="flex-1 text-left">Alertas de voz</span>
      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${cfg.enabled ? "bg-[#16A34A] text-white" : "bg-white/20 text-white/70"}`}>
        {cfg.enabled ? "ON" : "OFF"}
      </span>
    </button>
  );

  return (
    <>
      {trigger}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2"><Volume2 size={22} className="text-[#E85D04]" /> Alertas e impresión</DialogTitle>
            <DialogDescription>Ajustes de esta estación (se guardan solo en este dispositivo).</DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between gap-4 bg-[#F8FAFC] rounded-xl p-4 border border-[#111827]/10">
            <div>
              <p className="font-semibold text-[#1F2937] flex items-center gap-2"><Printer size={16} className="text-[#E85D04]" /> Imprimir comandas aquí</p>
              <p className="text-xs text-[#111827]/50">Actívalo solo en el equipo con impresora (la caja). Desactívalo en tablets de meseros.</p>
            </div>
            <Switch data-testid="print-comandas-switch" checked={printOn} onCheckedChange={(v) => { setPrintOn(v); setPrintComandas(v); }} />
          </div>

          {!voiceSupported ? (
            <div className="rounded-xl bg-[#F59E0B]/12 border border-[#F59E0B]/30 p-4 text-[#B45309] text-sm" data-testid="voice-unsupported">
              Este navegador no soporta alertas de voz. Usa Chrome o Edge actualizado.
            </div>
          ) : (
            <div className="space-y-5 py-2">
              <div className="flex items-center justify-between gap-4 bg-[#F8FAFC] rounded-xl p-4 border border-[#111827]/10">
                <div>
                  <p className="font-semibold text-[#1F2937]">Activar alertas de voz</p>
                  <p className="text-xs text-[#111827]/50">Anuncia los pedidos nuevos en voz alta.</p>
                </div>
                <Switch data-testid="voice-enabled-switch" checked={cfg.enabled} onCheckedChange={(v) => save({ ...cfg, enabled: v })} />
              </div>

              <div className={`bg-[#F8FAFC] rounded-xl p-4 border border-[#111827]/10 transition-opacity ${cfg.enabled ? "" : "opacity-50 pointer-events-none"}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-[#1F2937]">Volumen</p>
                  <span className="font-mono text-sm text-[#E85D04]" data-testid="voice-volume-label">{Math.round(cfg.volume * 100)}%</span>
                </div>
                <Slider data-testid="voice-volume-slider" value={[cfg.volume * 100]} min={0} max={100} step={5}
                  onValueChange={(v) => save({ ...cfg, volume: (v[0] || 0) / 100 })} />
              </div>

              <button data-testid="voice-test-btn" disabled={!cfg.enabled} onClick={speakTest}
                className="w-full h-12 rounded-xl bg-[#1F2937] text-white font-semibold hover:bg-black disabled:opacity-50 flex items-center justify-center gap-2">
                <Play size={18} /> Probar voz
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
